// src/pages/EditContentTaskPage.js
import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  doc, getDoc, updateDoc, serverTimestamp, collection, query, getDocs, orderBy, Timestamp
} from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from '../context/AuthContext';
import { useSocialLanguage } from '../hooks/useSocialLanguage';
import LanguageToggle from '../components/LanguageToggle';
import { getPlatformLabel, getContentTypeLabel } from '../utils/socialEnumLabels';
import styles from '../styles/EditContentTaskPage.module.css';

const managerRoles = ['Manager', 'Accounts Manager'];
const allowedViewRoles = ['Manager', 'Accounts Manager', 'Sales', 'Content Creator'];
const TRAINEE_ROLE_STRING = 'Trainee';

const PLATFORM_OPTIONS = ['Facebook', 'Instagram', 'TikTok', 'LinkedIn', 'X', 'YouTube', 'WhatsApp Channel'];
const CONTENT_TYPE_OPTIONS = ['Post', 'Reel', 'Video', 'Story', 'Carousel', 'Cover', 'Ad Creative', 'Campaign Content'];

const TRANSLATIONS = {
  en: {
    loadingForEdit: 'Loading content task for editing...',
    editTitlePrefix: 'Edit Content Task: ',
    loadingEllipsis: 'Loading...',
    backToContentTasks: 'Back to Content Tasks',
    couldNotLoad: 'Content task could not be loaded.',
    client: 'Client',
    searchClients: 'Search Clients...',
    noMatchingClients: 'No matching clients',
    selectOneClient: 'Select one client',
    platform: 'Platforms',
    platformsHint: 'Select one or more platforms to publish this content to.',
    contentType: 'Content Type',
    dueDate: 'Due Date',
    linkedPage: 'Linked Social Page (optional, required for Meta auto-publishing)',
    noPageLinked: '— No linked page —',
    noPagesForClientPlatform: 'No social pages found for this client/platform. Add one on the Client Social Pages page first.',
    title: 'Title',
    caption: 'Caption',
    hashtags: 'Hashtags',
    cta: 'Call To Action (optional)',
    assignTo: 'Assign To (Employees only)',
    searchEmployees: 'Search Employees...',
    holdCtrlCmd: 'Hold Ctrl/Cmd to select multiple',
    writerOptional: 'Writer (optional)',
    designerOptional: 'Designer (optional)',
    reviewerOptional: 'Reviewer (optional)',
    none: '— None —',
    cancel: 'Cancel',
    saveChanges: 'Save Changes',
    errSelectClient: 'Please select a Client.',
    errSelectPlatform: 'Please select at least one platform.',
    errTitleRequired: 'Title is required.',
    errDueDateRequired: 'Due Date is required.',
    errAssignAtLeastOne: 'Content task must be assigned to at least one employee.',
    errPermissionDenied: 'Permission denied during update.',
    errUpdateFailed: 'Failed to update content task. Please try again.',
    successUpdated: 'Content task updated successfully!',
  },
  ar: {
    loadingForEdit: 'جارٍ تحميل مهمة المحتوى للتعديل...',
    editTitlePrefix: 'تعديل مهمة المحتوى: ',
    loadingEllipsis: 'جارٍ التحميل...',
    backToContentTasks: 'العودة إلى مهام المحتوى',
    couldNotLoad: 'تعذر تحميل مهمة المحتوى.',
    client: 'العميل',
    searchClients: 'البحث عن عملاء...',
    noMatchingClients: 'لا يوجد عملاء مطابقون',
    selectOneClient: 'اختر عميلاً واحداً',
    platform: 'المنصات',
    platformsHint: 'اختر منصة واحدة أو أكثر لنشر هذا المحتوى عليها.',
    contentType: 'نوع المحتوى',
    dueDate: 'تاريخ الاستحقاق',
    linkedPage: 'الصفحة الاجتماعية المرتبطة (اختياري، مطلوب للنشر التلقائي عبر Meta)',
    noPageLinked: '— لا توجد صفحة مرتبطة —',
    noPagesForClientPlatform: 'لا توجد صفحات اجتماعية لهذا العميل/المنصة. أضف صفحة في صفحة "صفحات العميل الاجتماعية" أولاً.',
    title: 'العنوان',
    caption: 'النص المصاحب',
    hashtags: 'الوسوم',
    cta: 'دعوة لاتخاذ إجراء (اختياري)',
    assignTo: 'تعيين إلى (الموظفون فقط)',
    searchEmployees: 'البحث عن موظفين...',
    holdCtrlCmd: 'اضغط مع الاستمرار على Ctrl/Cmd لتحديد أكثر من عنصر',
    writerOptional: 'الكاتب (اختياري)',
    designerOptional: 'المصمم (اختياري)',
    reviewerOptional: 'المراجع (اختياري)',
    none: '— لا أحد —',
    cancel: 'إلغاء',
    saveChanges: 'حفظ التعديلات',
    errSelectClient: 'يرجى اختيار عميل.',
    errSelectPlatform: 'يرجى اختيار منصة واحدة على الأقل.',
    errTitleRequired: 'العنوان مطلوب.',
    errDueDateRequired: 'تاريخ الاستحقاق مطلوب.',
    errAssignAtLeastOne: 'يجب تعيين مهمة المحتوى لموظف واحد على الأقل.',
    errPermissionDenied: 'تم رفض الإذن خلال التحديث.',
    errUpdateFailed: 'فشل تحديث مهمة المحتوى. يرجى المحاولة مرة أخرى.',
    successUpdated: 'تم تحديث مهمة المحتوى بنجاح!',
  },
};

const isFiredOrInactive = (user) => {
  const inactiveFlag = user?.active === false || user?.isActive === false;
  const firedFlag = user?.isFired === true;
  const statusStr = String(user?.employmentStatus || user?.status || '').toLowerCase();
  const statusFlag = ['fired', 'terminated', 'inactive', 'disabled'].includes(statusStr);
  const endDate = user?.employmentEndDate ? new Date(user.employmentEndDate) : null;
  const endDateFlag = endDate && !isNaN(endDate.getTime()) ? endDate.getTime() <= Date.now() : false;
  return inactiveFlag || firedFlag || statusFlag || endDateFlag;
};

const formatDateForInput = (dateValue) => {
  if (!dateValue) return '';
  try {
    const date = (dateValue instanceof Timestamp) ? dateValue.toDate() : new Date(dateValue);
    if (isNaN(date.getTime())) return '';
    const adjustedDate = new Date(date.getTime() - (date.getTimezoneOffset() * 60000));
    return adjustedDate.toISOString().split('T')[0];
  } catch (error) {
    console.error('Error formatting date for input:', error);
    return '';
  }
};

const EditContentTaskPage = () => {
  const { taskId } = useParams();
  const navigate = useNavigate();
  const { currentUser, userRole } = useAuth();
  const { language, toggleLanguage, isRTL } = useSocialLanguage();
  const t = TRANSLATIONS[language];

  // Form state
  const [clientId, setClientId] = useState('');
  const [selectedPlatforms, setSelectedPlatforms] = useState([PLATFORM_OPTIONS[0]]);
  const [pageSelections, setPageSelections] = useState({}); // { [platform]: pageId }
  const [contentType, setContentType] = useState(CONTENT_TYPE_OPTIONS[0]);
  const [socialPages, setSocialPages] = useState([]);
  const [title, setTitle] = useState('');
  const [originalTitle, setOriginalTitle] = useState('');
  const [caption, setCaption] = useState('');
  const [hashtags, setHashtags] = useState('');
  const [cta, setCta] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [assignedTo, setAssignedTo] = useState([]);
  const [writerId, setWriterId] = useState('');
  const [designerId, setDesignerId] = useState('');
  const [reviewerId, setReviewerId] = useState('');

  const [initialTaskData, setInitialTaskData] = useState(null);

  const [employees, setEmployees] = useState([]);
  const [clients, setClients] = useState([]);
  const [managerUids, setManagerUids] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [clientSearchTerm, setClientSearchTerm] = useState('');
  const [assigneeSearchTerm, setAssigneeSearchTerm] = useState('');
  const [accessDenied, setAccessDenied] = useState(false);

  const isMounted = useRef(true);
  const successTimeoutRef = useRef(null);

  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
      if (successTimeoutRef.current) clearTimeout(successTimeoutRef.current);
    };
  }, []);

  // Fetch dropdown data
  useEffect(() => {
    const fetchInitialDropdownData = async () => {
      if (!isMounted.current) return;
      try {
        const usersCol = collection(db, 'users');
        const qAllUsers = query(usersCol, orderBy('name', 'asc'));
        const allUsersSnapshot = await getDocs(qAllUsers);
        if (!isMounted.current) return;

        const allUsersList = allUsersSnapshot.docs.map(d => ({ id: d.id, ...d.data() }));

        const employeeList = allUsersList
          .filter(u => u.role !== TRAINEE_ROLE_STRING && !isFiredOrInactive(u))
          .map(u => ({ id: u.id, name: u.name || u.email || u.id, email: u.email || '' }));
        setEmployees(employeeList);

        const managerIdList = allUsersList
          .filter(u => managerRoles.includes(u.role))
          .map(u => u.id);
        setManagerUids(managerIdList);

        const clientsRef = collection(db, 'clients');
        const qClients = query(clientsRef, orderBy('name', 'asc'));
        const clientsSnapshot = await getDocs(qClients);
        if (!isMounted.current) return;
        const clientList = clientsSnapshot.docs.map(d => ({ id: d.id, name: d.data().name }));
        setClients(clientList);

        const socialPagesSnapshot = await getDocs(collection(db, 'socialPages'));
        if (!isMounted.current) return;
        const socialPagesList = socialPagesSnapshot.docs.map(d => ({ id: d.id, ...d.data() }));
        setSocialPages(socialPagesList);
      } catch (err) {
        console.error('Error fetching initial data for dropdowns:', err);
        if (isMounted.current) setError(prev => prev || 'Could not load support data for the form.');
        if (isMounted.current) { setEmployees([]); setManagerUids([]); setClients([]); setSocialPages([]); }
      }
    };
    if (currentUser) fetchInitialDropdownData();
  }, [currentUser]);

  // Load content task
  useEffect(() => {
    if (!currentUser || !taskId) return;

    const loadContentTask = async () => {
      setLoading(true);
      setError('');
      setAccessDenied(false);
      try {
        const taskRef = doc(db, 'contentTasks', taskId);
        const docSnap = await getDoc(taskRef);

        if (!docSnap.exists()) {
          setError(`Content task with ID "${taskId}" not found.`);
          setInitialTaskData(null);
          setLoading(false);
          return;
        }

        const data = docSnap.data();

        const isManager = managerRoles.includes(userRole);
        const isCreator = data.createdBy === currentUser.uid;
        const isVisible = (data.visibleTo || []).includes(currentUser.uid);
        const canView = allowedViewRoles.includes(userRole) && (isManager || isCreator || isVisible);

        if (!canView) {
          setAccessDenied(true);
          setError('Access Denied: You do not have permission to edit this content task.');
          setLoading(false);
          return;
        }

        setInitialTaskData(data);
        setClientId(data.clientId || '');
        if (Array.isArray(data.targets) && data.targets.length > 0) {
          setSelectedPlatforms(data.targets.map(tg => tg.platform));
          const seededSelections = {};
          data.targets.forEach(tg => { if (tg.pageId) seededSelections[tg.platform] = tg.pageId; });
          setPageSelections(seededSelections);
        } else if (data.platform) {
          setSelectedPlatforms([data.platform]);
          setPageSelections(data.pageId ? { [data.platform]: data.pageId } : {});
        } else {
          setSelectedPlatforms([PLATFORM_OPTIONS[0]]);
          setPageSelections({});
        }
        setContentType(data.contentType || CONTENT_TYPE_OPTIONS[0]);
        setTitle(data.title || '');
        setOriginalTitle(data.title || '');
        setCaption(data.caption || '');
        setHashtags(data.hashtags || '');
        setCta(data.cta || '');
        setDueDate(formatDateForInput(data.dueDate));
        setAssignedTo([...(data.assignedTo || [])]);
        setWriterId(data.writerId || '');
        setDesignerId(data.designerId || '');
        setReviewerId(data.reviewerId || '');
      } catch (err) {
        console.error('Error fetching content task for edit:', err);
        setError('Failed to load content task data for editing.');
      } finally {
        if (isMounted.current) setLoading(false);
      }
    };

    loadContentTask();
  }, [taskId, currentUser, userRole]);

  const filteredClients = useMemo(() => {
    if (!clientSearchTerm) return clients;
    return clients.filter(c => (c.name || '').toLowerCase().includes(clientSearchTerm.toLowerCase()));
  }, [clients, clientSearchTerm]);

  const pagesForPlatform = useMemo(() => {
    const map = {};
    PLATFORM_OPTIONS.forEach(p => {
      map[p] = clientId ? socialPages.filter(sp => sp.clientId === clientId && sp.platform === p) : [];
    });
    return map;
  }, [socialPages, clientId]);

  const togglePlatform = (p) => {
    setSelectedPlatforms(prev => prev.includes(p) ? prev.filter(x => x !== p) : [...prev, p]);
  };

  // Keep pageSelections in sync: drop selections for unchecked/invalid platforms, and
  // auto-pick the page when a platform has exactly one social page for this client.
  useEffect(() => {
    setPageSelections(prev => {
      const next = {};
      selectedPlatforms.forEach(p => {
        const pages = pagesForPlatform[p] || [];
        const current = prev[p];
        if (current && pages.some(pg => pg.id === current)) {
          next[p] = current;
        } else if (pages.length === 1) {
          next[p] = pages[0].id;
        }
      });
      return next;
    });
  }, [selectedPlatforms, pagesForPlatform]);

  const filteredEmployees = useMemo(() => {
    if (!assigneeSearchTerm) return employees;
    return employees.filter(emp => (emp.name || '').toLowerCase().includes(assigneeSearchTerm.toLowerCase()));
  }, [employees, assigneeSearchTerm]);

  const handleAssigneeChange = (e) => {
    const { options } = e.target;
    const selected = [];
    for (let i = 0; i < options.length; i++) {
      if (options[i].selected) selected.push(options[i].value);
    }
    setAssignedTo(selected);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccessMessage('');

    if (!clientId) { setError(t.errSelectClient); return; }
    if (selectedPlatforms.length === 0) { setError(t.errSelectPlatform); return; }
    if (!title.trim()) { setError(t.errTitleRequired); return; }
    if (!dueDate) { setError(t.errDueDateRequired); return; }
    if (assignedTo.length === 0) { setError(t.errAssignAtLeastOne); return; }

    setUpdating(true);

    const selectedClient = clients.find(c => c.id === clientId);
    const clientName = selectedClient?.name || initialTaskData?.clientName || 'Unknown Client';

    try {
      const taskRef = doc(db, 'contentTasks', taskId);

      // visibleTo: creator + assignees + writer/designer/reviewer + managers
      const visibilitySet = new Set();
      if (initialTaskData?.createdBy) visibilitySet.add(initialTaskData.createdBy);
      assignedTo.forEach(uid => visibilitySet.add(uid));
      if (writerId) visibilitySet.add(writerId);
      if (designerId) visibilitySet.add(designerId);
      if (reviewerId) visibilitySet.add(reviewerId);
      managerUids.forEach(uid => visibilitySet.add(uid));
      const visibleToArray = Array.from(visibilitySet);

      const targets = selectedPlatforms.map(p => {
        const page = (pagesForPlatform[p] || []).find(pg => pg.id === pageSelections[p]);
        return { platform: p, pageId: page?.id || '', pageName: page?.pageName || '' };
      });

      const updates = {
        clientId,
        clientName,
        targets,
        platforms: selectedPlatforms,
        platform: selectedPlatforms.join(' + '),
        contentType,
        pageId: targets[0]?.pageId || '',
        pageName: targets[0]?.pageName || '',
        title: title.trim(),
        caption: caption.trim(),
        hashtags: hashtags.trim(),
        cta: cta.trim(),
        dueDate: Timestamp.fromDate(new Date(dueDate + 'T00:00:00')),
        assignedTo,
        writerId: writerId || null,
        designerId: designerId || null,
        reviewerId: reviewerId || null,
        visibleTo: visibleToArray,
        updatedAt: serverTimestamp(),
      };

      await updateDoc(taskRef, updates);

      setSuccessMessage(t.successUpdated);
      if (successTimeoutRef.current) clearTimeout(successTimeoutRef.current);
      successTimeoutRef.current = setTimeout(() => {
        if (isMounted.current) navigate(`/social/tasks/${taskId}`);
      }, 1200);
    } catch (err) {
      console.error('Error updating content task:', err);
      if (err.code === 'permission-denied') setError(t.errPermissionDenied);
      else setError(t.errUpdateFailed);
    } finally {
      if (isMounted.current) setUpdating(false);
    }
  };

  if (loading) {
    return <div className={styles.loadingIndicator}>{t.loadingForEdit}</div>;
  }

  if (accessDenied || (error && !initialTaskData)) {
    return (
      <div className={`${styles.pageContainer} ${styles.errorMessageContainer}`} dir={isRTL ? 'rtl' : 'ltr'}>
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '0.5rem' }}>
          <LanguageToggle language={language} onToggle={toggleLanguage} />
        </div>
        <p className={styles.errorMessage}>{error || t.couldNotLoad}</p>
        <button onClick={() => navigate('/social/tasks')} className={`${styles.formButton} ${styles.cancelButton}`}>
          {t.backToContentTasks}
        </button>
      </div>
    );
  }

  return (
    <div className={styles.pageContainer} dir={isRTL ? 'rtl' : 'ltr'}>
      <div className={styles.formBox}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '0.5rem' }}>
          <h2 className={styles.formTitle}>{t.editTitlePrefix}{originalTitle || t.loadingEllipsis}</h2>
          <LanguageToggle language={language} onToggle={toggleLanguage} />
        </div>

        {error && !successMessage && <p className={styles.errorMessage}>{error}</p>}
        {successMessage && <p className={styles.successMessage}>{successMessage}</p>}

        <form onSubmit={handleSubmit} className={styles.form}>
          <div className={styles.inputGroup}>
            <label htmlFor="client">{t.client}</label>
            <input
              type="text"
              placeholder={t.searchClients}
              value={clientSearchTerm}
              onChange={(e) => setClientSearchTerm(e.target.value)}
              disabled={updating || clients.length === 0}
              className={styles.searchInput}
            />
            <select
              id="client"
              value={clientId}
              onChange={(e) => { setClientId(e.target.value); }}
              required
              disabled={updating || clients.length === 0}
            >
              <option value="">{t.selectOneClient}</option>
              {filteredClients.length === 0 && <option disabled>{t.noMatchingClients}</option>}
              {filteredClients.map(client => (
                <option key={client.id} value={client.id}>{client.name}</option>
              ))}
            </select>
            <small className={styles.selectHelpText}>{t.selectOneClient}</small>
          </div>

          <div className={styles.inputGroup}>
            <label>{t.platform}</label>
            <small className={styles.selectHelpText}>{t.platformsHint}</small>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '0.6rem', marginTop: '0.4rem' }}>
              {PLATFORM_OPTIONS.map(p => {
                const pages = pagesForPlatform[p] || [];
                const checked = selectedPlatforms.includes(p);
                return (
                  <div key={p} style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: '0.6rem' }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', fontWeight: 'normal', minWidth: '9rem' }}>
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => togglePlatform(p)}
                        disabled={updating}
                      />
                      {getPlatformLabel(p, language)}
                    </label>
                    {checked && (
                      <select
                        value={pageSelections[p] || ''}
                        onChange={(e) => setPageSelections(prev => ({ ...prev, [p]: e.target.value }))}
                        disabled={updating || !clientId || pages.length === 0}
                        style={{ flex: '1 1 auto', minWidth: '12rem' }}
                      >
                        <option value="">{t.noPageLinked}</option>
                        {pages.map(page => (
                          <option key={page.id} value={page.id}>{page.pageName}</option>
                        ))}
                      </select>
                    )}
                    {checked && clientId && pages.length === 0 && (
                      <small className={styles.selectHelpText}>{t.noPagesForClientPlatform}</small>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          <div className={styles.gridThreeCols}>
            <div className={styles.inputGroup}>
              <label htmlFor="contentType">{t.contentType}</label>
              <select id="contentType" value={contentType} onChange={(e) => setContentType(e.target.value)} disabled={updating}>
                {CONTENT_TYPE_OPTIONS.map(ct => <option key={ct} value={ct}>{getContentTypeLabel(ct, language)}</option>)}
              </select>
            </div>
            <div className={styles.inputGroup}>
              <label htmlFor="dueDate">{t.dueDate}</label>
              <input
                type="date"
                id="dueDate"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                required
                disabled={updating}
              />
            </div>
          </div>

          <div className={styles.inputGroup}>
            <label htmlFor="title">{t.title}</label>
            <input
              type="text"
              id="title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              required
              disabled={updating}
            />
          </div>

          <div className={styles.inputGroup}>
            <label htmlFor="caption">{t.caption}</label>
            <textarea
              id="caption"
              value={caption}
              onChange={(e) => setCaption(e.target.value)}
              rows={5}
              disabled={updating}
            />
          </div>

          <div className={styles.gridThreeCols}>
            <div className={styles.inputGroup}>
              <label htmlFor="hashtags">{t.hashtags}</label>
              <input
                type="text"
                id="hashtags"
                value={hashtags}
                onChange={(e) => setHashtags(e.target.value)}
                disabled={updating}
              />
            </div>
            <div className={`${styles.inputGroup} ${styles.spanTwoCols}`}>
              <label htmlFor="cta">{t.cta}</label>
              <input
                type="text"
                id="cta"
                value={cta}
                onChange={(e) => setCta(e.target.value)}
                disabled={updating}
              />
            </div>
          </div>

          <div className={styles.inputGroup}>
            <label htmlFor="assignedTo">{t.assignTo}</label>
            <input
              type="text"
              placeholder={t.searchEmployees}
              value={assigneeSearchTerm}
              onChange={(e) => setAssigneeSearchTerm(e.target.value)}
              disabled={updating || employees.length === 0}
              className={styles.searchInput}
            />
            <select
              id="assignedTo"
              multiple
              value={assignedTo}
              onChange={handleAssigneeChange}
              size="6"
              disabled={updating || employees.length === 0}
              className={styles.multiSelectInput}
            >
              {filteredEmployees.map(emp => (
                <option key={emp.id} value={emp.id}>{emp.name}</option>
              ))}
            </select>
            <small className={styles.selectHelpText}>{t.holdCtrlCmd}</small>
          </div>

          <div className={styles.gridThreeCols}>
            <div className={styles.inputGroup}>
              <label htmlFor="writerId">{t.writerOptional}</label>
              <select id="writerId" value={writerId} onChange={(e) => setWriterId(e.target.value)} disabled={updating || employees.length === 0}>
                <option value="">{t.none}</option>
                {employees.map(emp => <option key={emp.id} value={emp.id}>{emp.name}</option>)}
              </select>
            </div>
            <div className={styles.inputGroup}>
              <label htmlFor="designerId">{t.designerOptional}</label>
              <select id="designerId" value={designerId} onChange={(e) => setDesignerId(e.target.value)} disabled={updating || employees.length === 0}>
                <option value="">{t.none}</option>
                {employees.map(emp => <option key={emp.id} value={emp.id}>{emp.name}</option>)}
              </select>
            </div>
            <div className={styles.inputGroup}>
              <label htmlFor="reviewerId">{t.reviewerOptional}</label>
              <select id="reviewerId" value={reviewerId} onChange={(e) => setReviewerId(e.target.value)} disabled={updating || employees.length === 0}>
                <option value="">{t.none}</option>
                {employees.map(emp => <option key={emp.id} value={emp.id}>{emp.name}</option>)}
              </select>
            </div>
          </div>

          <div className={styles.buttonGroup}>
            <button
              type="button"
              onClick={() => navigate(`/social/tasks/${taskId}`)}
              className={`${styles.formButton} ${styles.cancelButton}`}
              disabled={updating}
            >
              {t.cancel}
            </button>
            <button
              type="submit"
              className={`${styles.formButton} ${styles.submitButton}`}
              disabled={updating}
            >
              {updating ? <span className={styles.loadingSpinner}></span> : t.saveChanges}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditContentTaskPage;
